<template>
	<div class="page">
		<div class="grid grid-cols-1 gap-5.5 lg:grid-cols-10">
			<!-- Compact action cards (mobile / tablet only) -->
			<div class="order-1 grid grid-cols-3 gap-3 sm:gap-4 lg:hidden">
				<ActionCard
					icon="carbon:face-add"
					:title="$t(`dashboard.new_patient`)"
					vertical
					clickable
					@click="routePatients({ patientId: 'create' }).navigate()"
				/>
				<ActionCard
					:icon="ICONS.tools.pregnancy_calculator"
					:title="$t(`dashboard.obstetric_ruler`)"
					vertical
					clickable
					@click="routePregnancyCalculator().navigate()"
				/>
				<ActionCard
					:icon="ICONS.tools.biometry"
					:title="$t(`dashboard.biometry`)"
					vertical
					clickable
					@click="routeBiometry().navigate()"
				/>
			</div>

			<!-- Row 1 · left (70%): add patient -->
			<div class="hidden lg:order-0 lg:col-span-7 lg:block">
				<AddPatientCard class="min-h-full" />
			</div>

			<!-- Row 1 · right (30%): tools column -->
			<div class="hidden gap-5.5 lg:order-0 lg:col-span-3 lg:flex lg:flex-col">
				<ActionCard
					class="lg:flex-1"
					size="large"
					:icon="ICONS.tools.pregnancy_calculator"
					:title="$t(`dashboard.use_obstetric_ruler`)"
					:description="$t(`dashboard.use_obstetric_ruler_description`)"
					clickable
					@click="routePregnancyCalculator().navigate()"
				/>
				<ActionCard
					class="lg:flex-1"
					size="large"
					:icon="ICONS.tools.biometry"
					:title="$t(`dashboard.go_to_biometry`)"
					:description="$t(`dashboard.go_to_biometry_description`)"
					clickable
					@click="routeBiometry().navigate()"
				/>
			</div>

			<!-- Row 2 · sponsors -->
			<AdvBanner class="order-3 lg:order-0 lg:col-span-5" space-name="dashboard" size="large" />
			<AdvBanner class="hidden lg:order-0 lg:col-span-5 lg:block" space-name="dashboard_2" size="large" />

			<!-- Row 3 · left (70%): calendar -->
			<DashboardCalendar class="order-2 lg:order-0 lg:col-span-7" />

			<!-- Row 3 · right (30%): charts column -->
			<div class="order-4 flex flex-col gap-5.5 lg:order-0 lg:col-span-3">
				<PatientsChart class="lg:flex-1" :loading="loadingDashboard" :data="dashboard?.patients_timeline" />
				<PregnancyQuarters class="lg:flex-1" :loading="loadingDashboard" :data="dashboard?.trimesters" />
			</div>

			<!-- Row 4 · upcoming deliveries -->
			<PatientUpcomingDeliveriesList class="order-5 lg:order-0 lg:col-span-10" />

			<!-- TODO-NEXT: MVP2 attivare dialog per setup totp (questo ha scopo di testare la funzionalità)
				<div>
					<JMButton @click="showTotpDialog">Show totp dialog</JMButton>
				</div>
			-->
		</div>
	</div>
</template>

<script lang="ts" setup>
import type { I18nString } from "@/lang/config"
import type { Dashboard } from "@/types/dashboard"
import { useMessage } from "naive-ui"
import { onBeforeMount, ref } from "vue"
import { useI18n } from "vue-i18n"
import Api from "@/api"
import AdvBanner from "@/components/adv/banner"
import ActionCard from "@/components/common/ActionCard.vue"
import AddPatientCard from "@/components/dashboard/AddPatientCard.vue"
import DashboardCalendar from "@/components/dashboard/DashboardCalendar.vue"
import PatientsChart from "@/components/dashboard/PatientsChart.vue"
import PatientUpcomingDeliveriesList from "@/components/dashboard/PatientUpcomingDeliveriesList.vue"
import PregnancyQuarters from "@/components/dashboard/PregnancyQuarters.vue"
import { useNavigation } from "@/composables/router/useNavigation"
import { ICONS } from "@/const"
import { getApiErrorMessage } from "@/utils"

/* TODO-NEXT: MVP2 attivare dialog per setup totp (al caricamento del dashboard)
const dialog = useDialog()
const authStore = useAuthStore()
const { showModal } = useTotpSetupModal()
*/
const message = useMessage()
const { t } = useI18n()
const { routePatients, routePregnancyCalculator, routeBiometry } = useNavigation()
const dashboard = ref<Dashboard | null>(null)
const loadingDashboard = ref(false)

/* TODO-NEXT: MVP2 attivare dialog per setup totp
async function handleActivateTotp() {
	try {
		await showModal({
			onSuccess: () => {
				authStore.loadProfile()
			}
		})
	} catch {
		console.warn("totp setup aborted")
	}
}

function showTotpDialog() {
	infoDialog(dialog, {
		icon: () => h(Icon, { name: "carbon:two-factor-authentication", class: "text-default", size: 48 }),
		title: "Utilizza un’app di autenticazione e metti i tuoi dati al sicuro ✅",
		content: "L’autenticazione a due fattori serve per proteggere il tuo account. Bastano pochi minuti!",
		positiveText: "Cominciamo",
		negativeText: "Non chiedermelo più",
		success() {
			handleActivateTotp()
		},
		cancel() {
			authStore.stopAskTotpSetupDialog()
		}
	})
}
*/

function getDashboard() {
	loadingDashboard.value = true

	Api.common.dashboard
		.getDashboard()
		.then(response => {
			dashboard.value = response.data
		})
		.catch(err => {
			message.error(getApiErrorMessage(err) || t<I18nString>(`common.misc.generic_error`))
		})
		.finally(() => {
			loadingDashboard.value = false
		})
}

onBeforeMount(() => {
	getDashboard()
})
</script>
